import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { FiFileText, FiEye, FiX, FiExternalLink } from "react-icons/fi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { reportEventBus } from "@dd-report-components/report/ReportEvents";
import type { CitationsMap } from "@dd-report-components/report/Citations";

// Same worker setup as Components/DocumentViewer — idempotent if both mount.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

// The citation vocabulary, the inline `<CiteSup>` marker and the emitter that
// opens this panel live in the shared report package — BlockRenderer renders
// `{{cite:…}}` markers in both interfaces. This panel itself pulls in react-pdf
// for the document preview, so it stays in user-interface, the only app that
// mounts it.
export { CiteSup, CitationsContext, CiteMarker, openCitation } from "@dd-report-components/report/Citations";
export type { CitationSource, CitationEntry, CitationsMap } from "@dd-report-components/report/Citations";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(20, 50, 59, 0.22);
  animation: ${fadeIn} 0.18s ease;
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1301;
  width: min(430px, 94vw);
  background: #ffffff;
  border-left: 1px solid #e9eeeb;
  box-shadow: -16px 0 48px rgba(20, 50, 59, 0.16);
  overflow-y: auto;
  animation: ${slideIn} 0.26s cubic-bezier(0.2, 0.7, 0.2, 1);
`;

const PanelBody = styled.div`
  padding: 1.625rem 1.75rem 2.5rem;
`;

const PanelHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.375rem;
`;

const PanelOverline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.56rem;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #237a50;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  border: 1px solid #eef1ef;
  border-radius: 0.5rem;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa7ac;
`;

const RefTag = styled.div`
  font-family: ui-monospace, "IBM Plex Mono", monospace;
  font-size: 0.69rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #2c8c5e;
`;

const Title = styled.div`
  margin-top: 0.375rem;
  font-size: 1.1875rem;
  font-weight: 800;
  color: #14323b;
  letter-spacing: -0.02em;
  line-height: 1.25;
`;

const Detail = styled.p`
  margin: 1.25rem 0 0;
  font-size: 0.875rem;
  color: #4a5a60;
  line-height: 1.62;
`;

const PreviewBox = styled.div`
  margin-top: 1.375rem;
  border: 1px solid #e9eeeb;
  border-radius: 0.875rem;
  overflow: hidden;
  background: #f7f9f8;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.56rem 0.81rem;
  border-bottom: 1px solid #eef1ef;
  background: #ffffff;
`;

const PreviewLabel = styled.span`
  font-size: 0.59rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9aa7ac;
`;

const PreviewPdfWrapper = styled.div`
  display: flex;
  justify-content: center;
  background: #ffffff;
  max-height: 21.25rem;
  overflow-y: auto;

  .react-pdf__Page__canvas {
    max-width: 100%;
    height: auto !important;
  }
`;

const PreviewPdfFallback = styled.div`
  padding: 2rem;
  text-align: center;
  font-size: 0.78rem;
  color: #8a97a0;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  max-height: 22.5rem;
  object-fit: contain;
  background: #ffffff;
`;

const FileLinks = styled.div`
  margin-top: 1.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.56rem;
`;

const PrimaryLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.69rem;
  padding: 0.875rem 1rem;
  background: #eaf5ef;
  border: 1px solid #d4eadd;
  border-radius: 0.81rem;
  text-decoration: none;
  color: #237a50;
  font-size: 0.844rem;
  font-weight: 700;

  &:hover {
    background: #dcefe4;
  }
`;

const SecondaryLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.69rem;
  padding: 0.75rem 1rem;
  background: #fafbfa;
  border: 1px solid #ecefed;
  border-radius: 0.81rem;
  text-decoration: none;
  color: #5a6c72;
  font-size: 0.78rem;
  font-weight: 600;
`;

const LinkLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

const WhereBox = styled.div`
  margin-top: 1.375rem;
  padding: 1rem 1.125rem;
  background: #fafbfa;
  border: 1px solid #ecefed;
  border-radius: 0.81rem;
`;

const WhereKicker = styled.div`
  font-size: 0.59rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9aa7ac;
  margin-bottom: 0.5rem;
`;

const WhereRow = styled.div`
  display: flex;
  gap: 0.56rem;
  align-items: flex-start;
  font-size: 0.78rem;
  color: #5a6c72;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: #b7c2bd;
  }
`;

const Footer = styled.div`
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #eef1ef;
  font-size: 0.69rem;
  color: #a2afb4;
  line-height: 1.55;
`;

interface Props {
  citations: CitationsMap;
}

// Mounted once per report page. Listens for REPORT_OPEN_CITATION and renders
// whichever entry was requested — any <CiteSup> or {{cite:KEY}} marker
// anywhere in the report can trigger it, they don't need their own panel.
export const CitationSidePanel = ({ citations }: Props) => {
  const { t } = useTranslation();
  const [openKey, setOpenKey] = useState<string | null>(null);

  useEffect(() => {
    const onOpen = (key: string) => setOpenKey(key);
    reportEventBus.on("REPORT_OPEN_CITATION", onOpen);
    return () => {
      reportEventBus.off("REPORT_OPEN_CITATION", onOpen);
    };
  }, []);

  useEffect(() => {
    if (!openKey) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [openKey]);

  const entry = openKey ? citations[openKey] : null;
  if (!entry) return null;

  const close = () => setOpenKey(null);

  return (
    <>
      <Overlay onClick={close} />
      <Panel role="dialog" aria-modal="true" aria-label={t("dueDiligence.finalReport.citationPanel.ariaLabel")}>
        <PanelBody>
          <PanelHeaderRow>
            <PanelOverline>
              <FiFileText size={14} />
              {t("dueDiligence.finalReport.citationPanel.overline")}
            </PanelOverline>
            <CloseButton onClick={close} aria-label={t("dueDiligence.finalReport.citationPanel.close")}>
              <FiX size={16} />
            </CloseButton>
          </PanelHeaderRow>

          <div>
            <RefTag>{t("dueDiligence.finalReport.citationPanel.refLabel", { tag: entry.tag })}</RefTag>
            <Title>{entry.title}</Title>
          </div>

          <Detail>{entry.detail}</Detail>

          {entry.primary?.kind !== "link" && entry.primary && (
            <PreviewBox>
              <PreviewHeader>
                <FiEye size={13} color="#8a97a0" />
                <PreviewLabel>{t("dueDiligence.finalReport.citationPanel.previewLabel")}</PreviewLabel>
              </PreviewHeader>
              {entry.primary.kind === "pdf" ? (
                <PreviewPdfWrapper>
                  <Document
                    file={entry.primary.url}
                    loading={<PreviewPdfFallback>{t("common.documentViewer.loadingPdf")}</PreviewPdfFallback>}
                    error={<PreviewPdfFallback>{t("common.documentViewer.pdfErrorGeneric")}</PreviewPdfFallback>}
                  >
                    <Page pageNumber={1} width={366} />
                  </Document>
                </PreviewPdfWrapper>
              ) : (
                <PreviewImage src={entry.primary.url} alt={entry.title} />
              )}
            </PreviewBox>
          )}

          {(entry.primary || entry.secondary) && (
            <FileLinks>
              {entry.primary && (
                <PrimaryLink href={entry.primary.url} target="_blank" rel="noopener noreferrer">
                  <FiFileText size={18} />
                  <LinkLabel>{entry.primary.label}</LinkLabel>
                  <FiExternalLink size={14} />
                </PrimaryLink>
              )}
              {entry.secondary && (
                <SecondaryLink href={entry.secondary.url} target="_blank" rel="noopener noreferrer">
                  <FiFileText size={16} />
                  <LinkLabel>{entry.secondary.label}</LinkLabel>
                  <FiExternalLink size={13} />
                </SecondaryLink>
              )}
            </FileLinks>
          )}

          {entry.where && (
            <WhereBox>
              <WhereKicker>{t("dueDiligence.finalReport.citationPanel.whereLabel")}</WhereKicker>
              <WhereRow>
                <FiFileText size={14} />
                <span>{entry.where}</span>
              </WhereRow>
            </WhereBox>
          )}

          <Footer>{t("dueDiligence.finalReport.citationPanel.footer")}</Footer>
        </PanelBody>
      </Panel>
    </>
  );
};
